import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';

/** Documentación detallada (plantilla 12 apartados) — módulo Almacenes. */
export const almacenesSections = [
  functionDoc({
    id: "almacenes-seleccion",
    name: "Selección de almacén",
    type: "screen",
    route: "/warehouses",
    locationPath: "Almacenes > Almacenes",
    generalDescription: "Pantalla para elegir el almacén de trabajo. El sistema recuerda la selección para documentos, guías y reportes filtrados. Usuarios limitados solo ven almacenes asignados en su perfil.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Tarjetas o lista de almacenes",
        "controlType": "Listado",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Muestra nombre, código y accesos rápidos.",
        "behavior": ""
      },
      {
        "name": "Entrar al almacén",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Abre documentos y acciones del almacén.",
        "behavior": ""
      }
    ],
    procedure: [
      "Menú lateral Almacenes → Almacenes.",
      "Identifique su almacén en la lista.",
      "Haga clic para seleccionarlo o abrir documentos.",
      "Verifique en la cabecera que el almacén activo es el correcto antes de registrar guías."
    ],
    practicalExample: {
      "context": "Operador de tienda solo trabaja en «Almacén tienda 01».",
      "steps": [
        "Abra Almacenes",
        "Seleccione tienda 01",
        "Registre salidas del día"
      ]
    },
    validations: [
      "Sin almacén seleccionado algunos accesos redirigen aquí."
    ],
    expectedResult: [
      "Almacén activo definido para toda la sesión de inventario."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-documentos-list"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado, SHOTS.vistaGeneral],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-documentos-list",
    name: "Documentos por almacén",
    type: "list",
    route: "/warehouses",
    locationPath: "Almacenes > [Almacén] > Documentos",
    generalDescription: "Centro de operaciones del almacén: acceso a todos los tipos de guía (ingreso, salida, traslado, recepción, ajuste, inicial), conteo físico y atajos a reportes. Desde aquí navega a listados y formularios de cada tipo.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Pestañas o menú de tipos de guía",
        "controlType": "Navegación",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Organiza documentos por categoría.",
        "behavior": ""
      },
      {
        "name": "Acciones rápidas",
        "controlType": "Botones",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Nuevo ingreso, salida, etc.",
        "behavior": ""
      },
      {
        "name": "Conteo de inventario",
        "controlType": "Enlace",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Enlace al conteo físico del almacén.",
        "behavior": ""
      }
    ],
    procedure: [
      "Seleccione almacén en /warehouses.",
      "Abra Documentos.",
      "Elija el tipo de guía a consultar o crear.",
      "Use Nuevo en el tipo correspondiente."
    ],
    practicalExample: "El jefe de almacén revisa ingresos y salidas del día desde un solo lugar.",
    validations: [
      "Cada tipo de guía tiene validaciones propias al confirmar."
    ],
    expectedResult: [
      "Navegación clara hacia listados y altas de guías."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-ingreso-list",
      "almacenes-salida-list",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.vistaGeneral, SHOTS.listado],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-ingreso-list",
    name: "Guía de ingreso — listado",
    type: "list",
    route: "/warehouses",
    locationPath: "Almacenes > [Almacén] > Documentos > Guía de ingreso",
    generalDescription: "Consulta las guías de guía de ingreso del almacén activo desde la pantalla de documentos. Filtra por fecha o estado, abre el detalle para editar e imprimir, y accede al alta con el botón Nuevo. Entrada de mercadería por compra, devolución o ingreso positivo.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Selector de almacén",
        "controlType": "Cabecera / enlace",
        "required": false,
        "editable": false,
        "automatic": true,
        "description": "Muestra el almacén cuyos documentos está viendo.",
        "behavior": ""
      },
      {
        "name": "Filtros de guías",
        "controlType": "Filtros",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Acota por rango de fechas, número o estado.",
        "behavior": ""
      },
      {
        "name": "Tabla de guías",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Listado de guías de guía de ingreso con totales y usuario.",
        "behavior": ""
      },
      {
        "name": "Nuevo",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Abre el formulario de nueva guía de guía de ingreso.",
        "behavior": ""
      }
    ],
    procedure: [
      "Menú Almacenes → Almacenes y elija el almacén de trabajo.",
      "Entre a Documentos del almacén.",
      "Seleccione la pestaña o tipo Guía de ingreso.",
      "Use filtros si necesita localizar una guía.",
      "Pulse la fila o Editar para modificar; Imprimir si está disponible.",
      "Pulse Nuevo para registrar una guía nueva."
    ],
    practicalExample: {
      "context": "Supervisor revisa las guía de ingreso del día.",
      "data": {
        "Almacén": "Central",
        "Filtro": "Fecha de hoy"
      },
      "steps": [
        "Abra documentos",
        "Filtre Guía de ingreso",
        "Verifique totales contra físico"
      ]
    },
    validations: [
      "Solo ve guías del almacén seleccionado.",
      "Guías confirmadas pueden tener restricciones de edición según configuración."
    ],
    expectedResult: [
      "Listado actualizado de guías del tipo seleccionado.",
      "Acceso a creación y edición según permisos."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-ingreso-create",
      "almacenes-ingreso-edit",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado, SHOTS.vistaGeneral],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-ingreso-create",
    name: "Guía de ingreso — registro",
    type: "form",
    route: "/warehouses/1/entry-guides/create",
    locationPath: "Almacenes > Documentos > Guía de ingreso > Nuevo",
    generalDescription: "Formulario para registrar una guía de guía de ingreso. Entrada de mercadería por compra, devolución o ingreso positivo. Al confirmar, el sistema actualiza el kardex del almacén: aumenta saldo y actualiza costo promedio según configuración.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Fecha y serie",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Identifica el documento en listados y kardex.",
        "behavior": "La fecha afecta el período del movimiento."
      },
      {
        "name": "Proveedor / motivo / destino",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Según tipo de guía; vincula compra, proyecto o almacén destino.",
        "behavior": ""
      },
      {
        "name": "Líneas de producto",
        "controlType": "Grilla",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Producto, cantidad, unidad, costo o evento kardex.",
        "behavior": "Valida unidades y productos activos."
      },
      {
        "name": "Evento kardex",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Clasifica el movimiento para reportes y costo.",
        "behavior": ""
      },
      {
        "name": "Guardar / Confirmar",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Persiste la guía y genera movimientos.",
        "behavior": ""
      }
    ],
    procedure: [
      "Desde el listado de guías pulse Nuevo.",
      "Complete cabecera: fecha, referencias y datos obligatorios del tipo.",
      "Agregue líneas: busque producto, cantidad y datos de costo si aplica.",
      "Revise totales y evento kardex por línea.",
      "Guarde borrador o confirme según el flujo de su empresa.",
      "Verifique el kardex del producto si el movimiento es sensible."
    ],
    practicalExample: {
      "context": "Ejemplo de guía de ingreso operativa.",
      "data": {
        "Producto": "Harina 50 kg",
        "Cantidad": "10",
        "Evento": "Compra"
      },
      "steps": [
        "Nuevo",
        "Complete cabecera",
        "Agregue línea",
        "Confirme",
        "Revise kardex"
      ]
    },
    validations: [
      "Campos obligatorios de cabecera y al menos una línea.",
      "Stock y costos validados al confirmar salidas y valorizaciones.",
      "Referencias a OC o documentos deben existir si se exigen."
    ],
    expectedResult: [
      "Guía registrada y visible en documentos del almacén.",
      "Movimientos reflejados en kardex y reportes de entradas/salidas."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-ingreso-list",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto, SHOTS.confirmacion],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-ingreso-edit",
    name: "Guía de ingreso — edición",
    type: "form",
    route: "/entry-guides/:id/edit",
    locationPath: "Almacenes > Documentos > Guía de ingreso > Editar",
    generalDescription: "Permite corregir una guía de guía de ingreso en borrador o según permisos de edición. Los cambios en guías confirmadas pueden estar bloqueados; use guía de ajuste si la política lo exige.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Cabecera",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Datos generales de la guía existente.",
        "behavior": ""
      },
      {
        "name": "Líneas",
        "controlType": "Grilla",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Modificar cantidades o productos permitidos.",
        "behavior": ""
      },
      {
        "name": "Anular / Revertir",
        "controlType": "Botón",
        "required": false,
        "editable": false,
        "automatic": false,
        "description": "Si está habilitado para su rol.",
        "behavior": ""
      }
    ],
    procedure: [
      "Abra la guía desde el listado.",
      "Modifique campos permitidos.",
      "Guarde o confirme nuevamente.",
      "Si no puede editar, cree guía de ajuste compensatoria."
    ],
    practicalExample: "Corrija una cantidad mal digitada antes del cierre de inventario del día.",
    validations: [
      "Guías cerradas o vinculadas a ventas pueden bloquear edición."
    ],
    expectedResult: [
      "Kardex actualizado con los nuevos valores si la edición está permitida."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-ingreso-list",
      "almacenes-ingreso-create"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-salida-list",
    name: "Guía de salida — listado",
    type: "list",
    route: "/warehouses",
    locationPath: "Almacenes > [Almacén] > Documentos > Guía de salida",
    generalDescription: "Consulta las guías de guía de salida del almacén activo desde la pantalla de documentos. Filtra por fecha o estado, abre el detalle para editar e imprimir, y accede al alta con el botón Nuevo. Despacho por consumo, venta, producción u otros motivos.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Selector de almacén",
        "controlType": "Cabecera / enlace",
        "required": false,
        "editable": false,
        "automatic": true,
        "description": "Muestra el almacén cuyos documentos está viendo.",
        "behavior": ""
      },
      {
        "name": "Filtros de guías",
        "controlType": "Filtros",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Acota por rango de fechas, número o estado.",
        "behavior": ""
      },
      {
        "name": "Tabla de guías",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Listado de guías de guía de salida con totales y usuario.",
        "behavior": ""
      },
      {
        "name": "Nuevo",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Abre el formulario de nueva guía de guía de salida.",
        "behavior": ""
      }
    ],
    procedure: [
      "Menú Almacenes → Almacenes y elija el almacén de trabajo.",
      "Entre a Documentos del almacén.",
      "Seleccione la pestaña o tipo Guía de salida.",
      "Use filtros si necesita localizar una guía.",
      "Pulse la fila o Editar para modificar; Imprimir si está disponible.",
      "Pulse Nuevo para registrar una guía nueva."
    ],
    practicalExample: {
      "context": "Supervisor revisa las guía de salida del día.",
      "data": {
        "Almacén": "Central",
        "Filtro": "Fecha de hoy"
      },
      "steps": [
        "Abra documentos",
        "Filtre Guía de salida",
        "Verifique totales contra físico"
      ]
    },
    validations: [
      "Solo ve guías del almacén seleccionado.",
      "Guías confirmadas pueden tener restricciones de edición según configuración."
    ],
    expectedResult: [
      "Listado actualizado de guías del tipo seleccionado.",
      "Acceso a creación y edición según permisos."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-salida-create",
      "almacenes-salida-edit",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado, SHOTS.vistaGeneral],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-salida-create",
    name: "Guía de salida — registro",
    type: "form",
    route: "/warehouses/1/exit-guides/create",
    locationPath: "Almacenes > Documentos > Guía de salida > Nuevo",
    generalDescription: "Formulario para registrar una guía de guía de salida. Despacho por consumo, venta, producción u otros motivos. Al confirmar, el sistema actualiza el kardex del almacén: disminuye saldo; valida stock disponible.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Fecha y serie",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Identifica el documento en listados y kardex.",
        "behavior": "La fecha afecta el período del movimiento."
      },
      {
        "name": "Proveedor / motivo / destino",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Según tipo de guía; vincula compra, proyecto o almacén destino.",
        "behavior": ""
      },
      {
        "name": "Líneas de producto",
        "controlType": "Grilla",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Producto, cantidad, unidad, costo o evento kardex.",
        "behavior": "Valida unidades y productos activos."
      },
      {
        "name": "Evento kardex",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Clasifica el movimiento para reportes y costo.",
        "behavior": ""
      },
      {
        "name": "Guardar / Confirmar",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Persiste la guía y genera movimientos.",
        "behavior": ""
      }
    ],
    procedure: [
      "Desde el listado de guías pulse Nuevo.",
      "Complete cabecera: fecha, referencias y datos obligatorios del tipo.",
      "Agregue líneas: busque producto, cantidad y datos de costo si aplica.",
      "Revise totales y evento kardex por línea.",
      "Guarde borrador o confirme según el flujo de su empresa.",
      "Verifique el kardex del producto si el movimiento es sensible."
    ],
    practicalExample: {
      "context": "Ejemplo de guía de salida operativa.",
      "data": {
        "Producto": "Harina 50 kg",
        "Cantidad": "10",
        "Evento": "Compra"
      },
      "steps": [
        "Nuevo",
        "Complete cabecera",
        "Agregue línea",
        "Confirme",
        "Revise kardex"
      ]
    },
    validations: [
      "Campos obligatorios de cabecera y al menos una línea.",
      "Stock y costos validados al confirmar salidas y valorizaciones.",
      "Referencias a OC o documentos deben existir si se exigen."
    ],
    expectedResult: [
      "Guía registrada y visible en documentos del almacén.",
      "Movimientos reflejados en kardex y reportes de entradas/salidas."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-salida-list",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto, SHOTS.confirmacion],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-salida-edit",
    name: "Guía de salida — edición",
    type: "form",
    route: "/exit-guides/:id/edit",
    locationPath: "Almacenes > Documentos > Guía de salida > Editar",
    generalDescription: "Permite corregir una guía de guía de salida en borrador o según permisos de edición. Los cambios en guías confirmadas pueden estar bloqueados; use guía de ajuste si la política lo exige.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Cabecera",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Datos generales de la guía existente.",
        "behavior": ""
      },
      {
        "name": "Líneas",
        "controlType": "Grilla",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Modificar cantidades o productos permitidos.",
        "behavior": ""
      },
      {
        "name": "Anular / Revertir",
        "controlType": "Botón",
        "required": false,
        "editable": false,
        "automatic": false,
        "description": "Si está habilitado para su rol.",
        "behavior": ""
      }
    ],
    procedure: [
      "Abra la guía desde el listado.",
      "Modifique campos permitidos.",
      "Guarde o confirme nuevamente.",
      "Si no puede editar, cree guía de ajuste compensatoria."
    ],
    practicalExample: "Corrija una cantidad mal digitada antes del cierre de inventario del día.",
    validations: [
      "Guías cerradas o vinculadas a ventas pueden bloquear edición."
    ],
    expectedResult: [
      "Kardex actualizado con los nuevos valores si la edición está permitida."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-salida-list",
      "almacenes-salida-create"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-traslado-list",
    name: "Guía de traslado — listado",
    type: "list",
    route: "/warehouses",
    locationPath: "Almacenes > [Almacén] > Documentos > Guía de traslado",
    generalDescription: "Consulta las guías de guía de traslado del almacén activo desde la pantalla de documentos. Filtra por fecha o estado, abre el detalle para editar e imprimir, y accede al alta con el botón Nuevo. Mueve stock entre almacenes sin cambiar de empresa.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Selector de almacén",
        "controlType": "Cabecera / enlace",
        "required": false,
        "editable": false,
        "automatic": true,
        "description": "Muestra el almacén cuyos documentos está viendo.",
        "behavior": ""
      },
      {
        "name": "Filtros de guías",
        "controlType": "Filtros",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Acota por rango de fechas, número o estado.",
        "behavior": ""
      },
      {
        "name": "Tabla de guías",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Listado de guías de guía de traslado con totales y usuario.",
        "behavior": ""
      },
      {
        "name": "Nuevo",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Abre el formulario de nueva guía de guía de traslado.",
        "behavior": ""
      }
    ],
    procedure: [
      "Menú Almacenes → Almacenes y elija el almacén de trabajo.",
      "Entre a Documentos del almacén.",
      "Seleccione la pestaña o tipo Guía de traslado.",
      "Use filtros si necesita localizar una guía.",
      "Pulse la fila o Editar para modificar; Imprimir si está disponible.",
      "Pulse Nuevo para registrar una guía nueva."
    ],
    practicalExample: {
      "context": "Supervisor revisa las guía de traslado del día.",
      "data": {
        "Almacén": "Central",
        "Filtro": "Fecha de hoy"
      },
      "steps": [
        "Abra documentos",
        "Filtre Guía de traslado",
        "Verifique totales contra físico"
      ]
    },
    validations: [
      "Solo ve guías del almacén seleccionado.",
      "Guías confirmadas pueden tener restricciones de edición según configuración."
    ],
    expectedResult: [
      "Listado actualizado de guías del tipo seleccionado.",
      "Acceso a creación y edición según permisos."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-traslado-create",
      "almacenes-traslado-edit",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado, SHOTS.vistaGeneral],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-traslado-create",
    name: "Guía de traslado — registro",
    type: "form",
    route: "/warehouses/1/transfer-guides/create",
    locationPath: "Almacenes > Documentos > Guía de traslado > Nuevo",
    generalDescription: "Formulario para registrar una guía de guía de traslado. Mueve stock entre almacenes sin cambiar de empresa. Al confirmar, el sistema actualiza el kardex del almacén: salida en origen y entrada en destino al confirmar.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Fecha y serie",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Identifica el documento en listados y kardex.",
        "behavior": "La fecha afecta el período del movimiento."
      },
      {
        "name": "Proveedor / motivo / destino",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Según tipo de guía; vincula compra, proyecto o almacén destino.",
        "behavior": ""
      },
      {
        "name": "Líneas de producto",
        "controlType": "Grilla",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Producto, cantidad, unidad, costo o evento kardex.",
        "behavior": "Valida unidades y productos activos."
      },
      {
        "name": "Evento kardex",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Clasifica el movimiento para reportes y costo.",
        "behavior": ""
      },
      {
        "name": "Guardar / Confirmar",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Persiste la guía y genera movimientos.",
        "behavior": ""
      }
    ],
    procedure: [
      "Desde el listado de guías pulse Nuevo.",
      "Complete cabecera: fecha, referencias y datos obligatorios del tipo.",
      "Agregue líneas: busque producto, cantidad y datos de costo si aplica.",
      "Revise totales y evento kardex por línea.",
      "Guarde borrador o confirme según el flujo de su empresa.",
      "Verifique el kardex del producto si el movimiento es sensible."
    ],
    practicalExample: {
      "context": "Ejemplo de guía de traslado operativa.",
      "data": {
        "Producto": "Harina 50 kg",
        "Cantidad": "10",
        "Evento": "Compra"
      },
      "steps": [
        "Nuevo",
        "Complete cabecera",
        "Agregue línea",
        "Confirme",
        "Revise kardex"
      ]
    },
    validations: [
      "Campos obligatorios de cabecera y al menos una línea.",
      "Stock y costos validados al confirmar salidas y valorizaciones.",
      "Referencias a OC o documentos deben existir si se exigen."
    ],
    expectedResult: [
      "Guía registrada y visible en documentos del almacén.",
      "Movimientos reflejados en kardex y reportes de entradas/salidas."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-traslado-list",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto, SHOTS.confirmacion],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-traslado-edit",
    name: "Guía de traslado — edición",
    type: "form",
    route: "/transfer-guides/:id/edit",
    locationPath: "Almacenes > Documentos > Guía de traslado > Editar",
    generalDescription: "Permite corregir una guía de guía de traslado en borrador o según permisos de edición. Los cambios en guías confirmadas pueden estar bloqueados; use guía de ajuste si la política lo exige.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Cabecera",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Datos generales de la guía existente.",
        "behavior": ""
      },
      {
        "name": "Líneas",
        "controlType": "Grilla",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Modificar cantidades o productos permitidos.",
        "behavior": ""
      },
      {
        "name": "Anular / Revertir",
        "controlType": "Botón",
        "required": false,
        "editable": false,
        "automatic": false,
        "description": "Si está habilitado para su rol.",
        "behavior": ""
      }
    ],
    procedure: [
      "Abra la guía desde el listado.",
      "Modifique campos permitidos.",
      "Guarde o confirme nuevamente.",
      "Si no puede editar, cree guía de ajuste compensatoria."
    ],
    practicalExample: "Corrija una cantidad mal digitada antes del cierre de inventario del día.",
    validations: [
      "Guías cerradas o vinculadas a ventas pueden bloquear edición."
    ],
    expectedResult: [
      "Kardex actualizado con los nuevos valores si la edición está permitida."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-traslado-list",
      "almacenes-traslado-create"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-recepcion-list",
    name: "Guía de recepción — listado",
    type: "list",
    route: "/warehouses",
    locationPath: "Almacenes > [Almacén] > Documentos > Guía de recepción",
    generalDescription: "Consulta las guías de guía de recepción del almacén activo desde la pantalla de documentos. Filtra por fecha o estado, abre el detalle para editar e imprimir, y accede al alta con el botón Nuevo. Recepción de traslado pendiente en almacén destino.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Selector de almacén",
        "controlType": "Cabecera / enlace",
        "required": false,
        "editable": false,
        "automatic": true,
        "description": "Muestra el almacén cuyos documentos está viendo.",
        "behavior": ""
      },
      {
        "name": "Filtros de guías",
        "controlType": "Filtros",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Acota por rango de fechas, número o estado.",
        "behavior": ""
      },
      {
        "name": "Tabla de guías",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Listado de guías de guía de recepción con totales y usuario.",
        "behavior": ""
      },
      {
        "name": "Nuevo",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Abre el formulario de nueva guía de guía de recepción.",
        "behavior": ""
      }
    ],
    procedure: [
      "Menú Almacenes → Almacenes y elija el almacén de trabajo.",
      "Entre a Documentos del almacén.",
      "Seleccione la pestaña o tipo Guía de recepción.",
      "Use filtros si necesita localizar una guía.",
      "Pulse la fila o Editar para modificar; Imprimir si está disponible.",
      "Pulse Nuevo para registrar una guía nueva."
    ],
    practicalExample: {
      "context": "Supervisor revisa las guía de recepción del día.",
      "data": {
        "Almacén": "Central",
        "Filtro": "Fecha de hoy"
      },
      "steps": [
        "Abra documentos",
        "Filtre Guía de recepción",
        "Verifique totales contra físico"
      ]
    },
    validations: [
      "Solo ve guías del almacén seleccionado.",
      "Guías confirmadas pueden tener restricciones de edición según configuración."
    ],
    expectedResult: [
      "Listado actualizado de guías del tipo seleccionado.",
      "Acceso a creación y edición según permisos."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-recepcion-create",
      "almacenes-recepcion-edit",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado, SHOTS.vistaGeneral],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-recepcion-create",
    name: "Guía de recepción — registro",
    type: "form",
    route: "/warehouses/1/reception-guides/create",
    locationPath: "Almacenes > Documentos > Guía de recepción > Nuevo",
    generalDescription: "Formulario para registrar una guía de guía de recepción. Recepción de traslado pendiente en almacén destino. Al confirmar, el sistema actualiza el kardex del almacén: incrementa stock en destino al completar recepción.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Fecha y serie",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Identifica el documento en listados y kardex.",
        "behavior": "La fecha afecta el período del movimiento."
      },
      {
        "name": "Proveedor / motivo / destino",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Según tipo de guía; vincula compra, proyecto o almacén destino.",
        "behavior": ""
      },
      {
        "name": "Líneas de producto",
        "controlType": "Grilla",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Producto, cantidad, unidad, costo o evento kardex.",
        "behavior": "Valida unidades y productos activos."
      },
      {
        "name": "Evento kardex",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Clasifica el movimiento para reportes y costo.",
        "behavior": ""
      },
      {
        "name": "Guardar / Confirmar",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Persiste la guía y genera movimientos.",
        "behavior": ""
      }
    ],
    procedure: [
      "Desde el listado de guías pulse Nuevo.",
      "Complete cabecera: fecha, referencias y datos obligatorios del tipo.",
      "Agregue líneas: busque producto, cantidad y datos de costo si aplica.",
      "Revise totales y evento kardex por línea.",
      "Guarde borrador o confirme según el flujo de su empresa.",
      "Verifique el kardex del producto si el movimiento es sensible."
    ],
    practicalExample: {
      "context": "Ejemplo de guía de recepción operativa.",
      "data": {
        "Producto": "Harina 50 kg",
        "Cantidad": "10",
        "Evento": "Compra"
      },
      "steps": [
        "Nuevo",
        "Complete cabecera",
        "Agregue línea",
        "Confirme",
        "Revise kardex"
      ]
    },
    validations: [
      "Campos obligatorios de cabecera y al menos una línea.",
      "Stock y costos validados al confirmar salidas y valorizaciones.",
      "Referencias a OC o documentos deben existir si se exigen."
    ],
    expectedResult: [
      "Guía registrada y visible en documentos del almacén.",
      "Movimientos reflejados en kardex y reportes de entradas/salidas."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-recepcion-list",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto, SHOTS.confirmacion],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-recepcion-edit",
    name: "Guía de recepción — edición",
    type: "form",
    route: "/reception-guides/:id/edit",
    locationPath: "Almacenes > Documentos > Guía de recepción > Editar",
    generalDescription: "Permite corregir una guía de guía de recepción en borrador o según permisos de edición. Los cambios en guías confirmadas pueden estar bloqueados; use guía de ajuste si la política lo exige.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Cabecera",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Datos generales de la guía existente.",
        "behavior": ""
      },
      {
        "name": "Líneas",
        "controlType": "Grilla",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Modificar cantidades o productos permitidos.",
        "behavior": ""
      },
      {
        "name": "Anular / Revertir",
        "controlType": "Botón",
        "required": false,
        "editable": false,
        "automatic": false,
        "description": "Si está habilitado para su rol.",
        "behavior": ""
      }
    ],
    procedure: [
      "Abra la guía desde el listado.",
      "Modifique campos permitidos.",
      "Guarde o confirme nuevamente.",
      "Si no puede editar, cree guía de ajuste compensatoria."
    ],
    practicalExample: "Corrija una cantidad mal digitada antes del cierre de inventario del día.",
    validations: [
      "Guías cerradas o vinculadas a ventas pueden bloquear edición."
    ],
    expectedResult: [
      "Kardex actualizado con los nuevos valores si la edición está permitida."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-recepcion-list",
      "almacenes-recepcion-create"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-ajuste-list",
    name: "Guía de ajuste — listado",
    type: "list",
    route: "/warehouses",
    locationPath: "Almacenes > [Almacén] > Documentos > Guía de ajuste",
    generalDescription: "Consulta las guías de guía de ajuste del almacén activo desde la pantalla de documentos. Filtra por fecha o estado, abre el detalle para editar e imprimir, y accede al alta con el botón Nuevo. Corrige sobrantes o faltantes de inventario.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Selector de almacén",
        "controlType": "Cabecera / enlace",
        "required": false,
        "editable": false,
        "automatic": true,
        "description": "Muestra el almacén cuyos documentos está viendo.",
        "behavior": ""
      },
      {
        "name": "Filtros de guías",
        "controlType": "Filtros",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Acota por rango de fechas, número o estado.",
        "behavior": ""
      },
      {
        "name": "Tabla de guías",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Listado de guías de guía de ajuste con totales y usuario.",
        "behavior": ""
      },
      {
        "name": "Nuevo",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Abre el formulario de nueva guía de guía de ajuste.",
        "behavior": ""
      }
    ],
    procedure: [
      "Menú Almacenes → Almacenes y elija el almacén de trabajo.",
      "Entre a Documentos del almacén.",
      "Seleccione la pestaña o tipo Guía de ajuste.",
      "Use filtros si necesita localizar una guía.",
      "Pulse la fila o Editar para modificar; Imprimir si está disponible.",
      "Pulse Nuevo para registrar una guía nueva."
    ],
    practicalExample: {
      "context": "Supervisor revisa las guía de ajuste del día.",
      "data": {
        "Almacén": "Central",
        "Filtro": "Fecha de hoy"
      },
      "steps": [
        "Abra documentos",
        "Filtre Guía de ajuste",
        "Verifique totales contra físico"
      ]
    },
    validations: [
      "Solo ve guías del almacén seleccionado.",
      "Guías confirmadas pueden tener restricciones de edición según configuración."
    ],
    expectedResult: [
      "Listado actualizado de guías del tipo seleccionado.",
      "Acceso a creación y edición según permisos."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-ajuste-create",
      "almacenes-ajuste-edit",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado, SHOTS.vistaGeneral],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-ajuste-create",
    name: "Guía de ajuste — registro",
    type: "form",
    route: "/warehouses/1/adjustment-guides/create",
    locationPath: "Almacenes > Documentos > Guía de ajuste > Nuevo",
    generalDescription: "Formulario para registrar una guía de guía de ajuste. Corrige sobrantes o faltantes de inventario. Al confirmar, el sistema actualiza el kardex del almacén: ajusta cantidad y valor según líneas positivas o negativas.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Fecha y serie",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Identifica el documento en listados y kardex.",
        "behavior": "La fecha afecta el período del movimiento."
      },
      {
        "name": "Proveedor / motivo / destino",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Según tipo de guía; vincula compra, proyecto o almacén destino.",
        "behavior": ""
      },
      {
        "name": "Líneas de producto",
        "controlType": "Grilla",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Producto, cantidad, unidad, costo o evento kardex.",
        "behavior": "Valida unidades y productos activos."
      },
      {
        "name": "Evento kardex",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Clasifica el movimiento para reportes y costo.",
        "behavior": ""
      },
      {
        "name": "Guardar / Confirmar",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Persiste la guía y genera movimientos.",
        "behavior": ""
      }
    ],
    procedure: [
      "Desde el listado de guías pulse Nuevo.",
      "Complete cabecera: fecha, referencias y datos obligatorios del tipo.",
      "Agregue líneas: busque producto, cantidad y datos de costo si aplica.",
      "Revise totales y evento kardex por línea.",
      "Guarde borrador o confirme según el flujo de su empresa.",
      "Verifique el kardex del producto si el movimiento es sensible."
    ],
    practicalExample: {
      "context": "Ejemplo de guía de ajuste operativa.",
      "data": {
        "Producto": "Harina 50 kg",
        "Cantidad": "10",
        "Evento": "Compra"
      },
      "steps": [
        "Nuevo",
        "Complete cabecera",
        "Agregue línea",
        "Confirme",
        "Revise kardex"
      ]
    },
    validations: [
      "Campos obligatorios de cabecera y al menos una línea.",
      "Stock y costos validados al confirmar salidas y valorizaciones.",
      "Referencias a OC o documentos deben existir si se exigen."
    ],
    expectedResult: [
      "Guía registrada y visible en documentos del almacén.",
      "Movimientos reflejados en kardex y reportes de entradas/salidas."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-ajuste-list",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto, SHOTS.confirmacion],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-ajuste-edit",
    name: "Guía de ajuste — edición",
    type: "form",
    route: "/adjustment-guides/:id/edit",
    locationPath: "Almacenes > Documentos > Guía de ajuste > Editar",
    generalDescription: "Permite corregir una guía de guía de ajuste en borrador o según permisos de edición. Los cambios en guías confirmadas pueden estar bloqueados; use guía de ajuste si la política lo exige.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Cabecera",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Datos generales de la guía existente.",
        "behavior": ""
      },
      {
        "name": "Líneas",
        "controlType": "Grilla",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Modificar cantidades o productos permitidos.",
        "behavior": ""
      },
      {
        "name": "Anular / Revertir",
        "controlType": "Botón",
        "required": false,
        "editable": false,
        "automatic": false,
        "description": "Si está habilitado para su rol.",
        "behavior": ""
      }
    ],
    procedure: [
      "Abra la guía desde el listado.",
      "Modifique campos permitidos.",
      "Guarde o confirme nuevamente.",
      "Si no puede editar, cree guía de ajuste compensatoria."
    ],
    practicalExample: "Corrija una cantidad mal digitada antes del cierre de inventario del día.",
    validations: [
      "Guías cerradas o vinculadas a ventas pueden bloquear edición."
    ],
    expectedResult: [
      "Kardex actualizado con los nuevos valores si la edición está permitida."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-ajuste-list",
      "almacenes-ajuste-create"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-inicial-list",
    name: "Guía inicial — listado",
    type: "list",
    route: "/warehouses",
    locationPath: "Almacenes > [Almacén] > Documentos > Guía inicial",
    generalDescription: "Consulta las guías de guía inicial del almacén activo desde la pantalla de documentos. Filtra por fecha o estado, abre el detalle para editar e imprimir, y accede al alta con el botón Nuevo. Carga de saldo inicial de productos al implementar kardex.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Selector de almacén",
        "controlType": "Cabecera / enlace",
        "required": false,
        "editable": false,
        "automatic": true,
        "description": "Muestra el almacén cuyos documentos está viendo.",
        "behavior": ""
      },
      {
        "name": "Filtros de guías",
        "controlType": "Filtros",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Acota por rango de fechas, número o estado.",
        "behavior": ""
      },
      {
        "name": "Tabla de guías",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Listado de guías de guía inicial con totales y usuario.",
        "behavior": ""
      },
      {
        "name": "Nuevo",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Abre el formulario de nueva guía de guía inicial.",
        "behavior": ""
      }
    ],
    procedure: [
      "Menú Almacenes → Almacenes y elija el almacén de trabajo.",
      "Entre a Documentos del almacén.",
      "Seleccione la pestaña o tipo Guía inicial.",
      "Use filtros si necesita localizar una guía.",
      "Pulse la fila o Editar para modificar; Imprimir si está disponible.",
      "Pulse Nuevo para registrar una guía nueva."
    ],
    practicalExample: {
      "context": "Supervisor revisa las guía inicial del día.",
      "data": {
        "Almacén": "Central",
        "Filtro": "Fecha de hoy"
      },
      "steps": [
        "Abra documentos",
        "Filtre Guía inicial",
        "Verifique totales contra físico"
      ]
    },
    validations: [
      "Solo ve guías del almacén seleccionado.",
      "Guías confirmadas pueden tener restricciones de edición según configuración."
    ],
    expectedResult: [
      "Listado actualizado de guías del tipo seleccionado.",
      "Acceso a creación y edición según permisos."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-inicial-create",
      "almacenes-inicial-edit",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado, SHOTS.vistaGeneral],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-inicial-create",
    name: "Guía inicial — registro",
    type: "form",
    route: "/warehouses/1/initial-guides/create",
    locationPath: "Almacenes > Documentos > Guía inicial > Nuevo",
    generalDescription: "Formulario para registrar una guía de guía inicial. Carga de saldo inicial de productos al implementar kardex. Al confirmar, el sistema actualiza el kardex del almacén: establece saldo y costo de partida; use solo en implementación o reapertura.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Fecha y serie",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Identifica el documento en listados y kardex.",
        "behavior": "La fecha afecta el período del movimiento."
      },
      {
        "name": "Proveedor / motivo / destino",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Según tipo de guía; vincula compra, proyecto o almacén destino.",
        "behavior": ""
      },
      {
        "name": "Líneas de producto",
        "controlType": "Grilla",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Producto, cantidad, unidad, costo o evento kardex.",
        "behavior": "Valida unidades y productos activos."
      },
      {
        "name": "Evento kardex",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Clasifica el movimiento para reportes y costo.",
        "behavior": ""
      },
      {
        "name": "Guardar / Confirmar",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Persiste la guía y genera movimientos.",
        "behavior": ""
      }
    ],
    procedure: [
      "Desde el listado de guías pulse Nuevo.",
      "Complete cabecera: fecha, referencias y datos obligatorios del tipo.",
      "Agregue líneas: busque producto, cantidad y datos de costo si aplica.",
      "Revise totales y evento kardex por línea.",
      "Guarde borrador o confirme según el flujo de su empresa.",
      "Verifique el kardex del producto si el movimiento es sensible."
    ],
    practicalExample: {
      "context": "Ejemplo de guía inicial operativa.",
      "data": {
        "Producto": "Harina 50 kg",
        "Cantidad": "10",
        "Evento": "Compra"
      },
      "steps": [
        "Nuevo",
        "Complete cabecera",
        "Agregue línea",
        "Confirme",
        "Revise kardex"
      ]
    },
    validations: [
      "Campos obligatorios de cabecera y al menos una línea.",
      "Stock y costos validados al confirmar salidas y valorizaciones.",
      "Referencias a OC o documentos deben existir si se exigen."
    ],
    expectedResult: [
      "Guía registrada y visible en documentos del almacén.",
      "Movimientos reflejados en kardex y reportes de entradas/salidas."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-inicial-list",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto, SHOTS.confirmacion],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-inicial-edit",
    name: "Guía inicial — edición",
    type: "form",
    route: "/initial-guides/:id/edit",
    locationPath: "Almacenes > Documentos > Guía inicial > Editar",
    generalDescription: "Permite corregir una guía de guía inicial en borrador o según permisos de edición. Los cambios en guías confirmadas pueden estar bloqueados; use guía de ajuste si la política lo exige.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Cabecera",
        "controlType": "Campo",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Datos generales de la guía existente.",
        "behavior": ""
      },
      {
        "name": "Líneas",
        "controlType": "Grilla",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Modificar cantidades o productos permitidos.",
        "behavior": ""
      },
      {
        "name": "Anular / Revertir",
        "controlType": "Botón",
        "required": false,
        "editable": false,
        "automatic": false,
        "description": "Si está habilitado para su rol.",
        "behavior": ""
      }
    ],
    procedure: [
      "Abra la guía desde el listado.",
      "Modifique campos permitidos.",
      "Guarde o confirme nuevamente.",
      "Si no puede editar, cree guía de ajuste compensatoria."
    ],
    practicalExample: "Corrija una cantidad mal digitada antes del cierre de inventario del día.",
    validations: [
      "Guías cerradas o vinculadas a ventas pueden bloquear edición."
    ],
    expectedResult: [
      "Kardex actualizado con los nuevos valores si la edición está permitida."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-inicial-list",
      "almacenes-inicial-create"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-conteo",
    name: "Conteo de inventario (almacén)",
    type: "form",
    route: "/warehouses",
    locationPath: "Almacenes > [Almacén] > Conteo de inventario",
    generalDescription: "Registro de conteo físico por producto en el almacén. Puede alimentar ajustes o campañas de auditoría según configuración de su empresa.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Producto y cantidad contada",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Comparación con saldo sistema.",
        "behavior": ""
      },
      {
        "name": "Guardar conteo",
        "controlType": "Botón",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Persiste avance del conteo.",
        "behavior": ""
      }
    ],
    procedure: [
      "Desde documentos del almacén abra Conteo.",
      "Registre cantidades.",
      "Genere ajuste si procede."
    ],
    practicalExample: "Conteo mensual de categoría secos.",
    validations: [
      "Producto debe pertenecer al almacén."
    ],
    expectedResult: [
      "Registro de conteo para conciliación."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-ajuste-create",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.formularioCompleto],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-report-inventory-dashboard",
    name: "Dashboard de inventario",
    type: "report",
    route: "/reports/inventory-dashboard",
    locationPath: "Almacenes > Reportes > Dashboard de inventario",
    generalDescription: "Panel con indicadores de stock, rotación y alertas agregadas por almacén y período. Sirve para detectar quiebres y concentración de valor.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Filtro almacén",
        "controlType": "Select",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Uno o varios almacenes.",
        "behavior": ""
      },
      {
        "name": "Período",
        "controlType": "Fechas",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Rango de fechas para tendencias.",
        "behavior": ""
      },
      {
        "name": "Gráficos y KPIs",
        "controlType": "Dashboard",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Visualización de métricas.",
        "behavior": ""
      }
    ],
    procedure: [
      "Almacenes → Reportes → Dashboard de inventario.",
      "Seleccione almacenes y fechas.",
      "Analice KPIs y gráficos."
    ],
    practicalExample: "Gerente identifica productos sin rotación en el trimestre.",
    validations: [
      "Datos dependen de kardex actualizado."
    ],
    expectedResult: [
      "Vista ejecutiva del inventario."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-kardex",
      "almacenes-report-stock"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.vistaGeneral],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-report-entry-change-alerts",
    name: "Alertas de cambio en ingresos",
    type: "report",
    route: "/reports/entry-change-alerts",
    locationPath: "Almacenes > Reportes > Alertas cambio en ingresos",
    generalDescription: "Lista ingresos cuyo costo o cantidad fue modificado después del registro inicial, para control interno y auditoría de compras-almacén.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Filtros fecha / almacén",
        "controlType": "Filtros",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Acota alertas.",
        "behavior": ""
      },
      {
        "name": "Tabla de alertas",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Muestra guía, producto y variación.",
        "behavior": ""
      }
    ],
    procedure: [
      "Abra el reporte desde Almacenes → Reportes.",
      "Filtre período.",
      "Investigue cada alerta con el área de compras."
    ],
    practicalExample: "Auditor revisa cambios de costo en ingresos de la semana.",
    validations: [
      "Solo muestra movimientos con historial de cambio."
    ],
    expectedResult: [
      "Listado de ingresos con modificaciones posteriores."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-ingreso-list",
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-kardex",
    name: "Kardex",
    type: "report",
    route: "/reports/kardex",
    locationPath: "Almacenes > Reportes > Kardex",
    generalDescription: "Historial cronológico de entradas, salidas y saldo valorizado por producto y almacén. Es la fuente de verdad operativa del inventario.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Almacén y producto",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Obligatorios para consultar.",
        "behavior": ""
      },
      {
        "name": "Rango de fechas",
        "controlType": "Fechas",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Acota movimientos.",
        "behavior": ""
      },
      {
        "name": "Tabla kardex",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Saldo, costo y documento origen.",
        "behavior": ""
      },
      {
        "name": "Detalle / Recalcular",
        "controlType": "Enlace",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Enlaces a detalle y recálculo (permiso).",
        "behavior": ""
      }
    ],
    procedure: [
      "Almacenes → Reportes → Kardex.",
      "Seleccione almacén y producto.",
      "Indique fechas y consulte.",
      "Abra detalle de fila para ver documento origen."
    ],
    practicalExample: {
      "context": "Analizar saldo de aceite en almacén central.",
      "steps": [
        "Filtre producto",
        "Revise última salida"
      ]
    },
    validations: [
      "Producto inactivo puede no mostrar movimientos recientes."
    ],
    expectedResult: [
      "Saldo y costo coherente con guías confirmadas."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-kardex-inconsistencias",
      "almacenes-kardex-recalcular"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-kardex-recalcular",
    name: "Recalcular kardex",
    type: "form",
    route: "/reports/kardex",
    locationPath: "Almacenes > Reportes > Kardex > Recalcular",
    generalDescription: "Proceso administrativo que reconstruye saldos y costos del kardex para un producto-almacén. Use solo con autorización de contabilidad o administración.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Confirmación",
        "controlType": "Diálogo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Advierte impacto en costos.",
        "behavior": ""
      }
    ],
    procedure: [
      "Desde detalle de kardex elija Recalcular.",
      "Confirme.",
      "Espere finalización.",
      "Verifique saldo."
    ],
    practicalExample: "Tras corregir guías antiguas, contabilidad solicita recálculo.",
    validations: [
      "Puede tardar en productos con muchos movimientos."
    ],
    expectedResult: [
      "Kardex recalculado según guías existentes."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-kardex"
    ],
    observations: [ROLES.admin, ROLES.limitado, "Operación sensible; documente el motivo."],
    screenshots: [SHOTS.confirmacion],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-kardex-inconsistencias",
    name: "Inconsistencias de kardex",
    type: "report",
    route: "/reports/kardex-inconsistencies",
    locationPath: "Almacenes > Reportes > Inconsistencias kardex",
    generalDescription: "Detecta saldos negativos, descuadres entre guías y kardex u otras reglas configuradas. Punto de partida para ajustes o recálculo.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Filtros",
        "controlType": "Filtros",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Almacén, producto, severidad.",
        "behavior": ""
      },
      {
        "name": "Resultados",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Lista de inconsistencias.",
        "behavior": ""
      }
    ],
    procedure: [
      "Ejecute el reporte.",
      "Priorice saldos negativos.",
      "Corrija con guías o recálculo."
    ],
    practicalExample: "Cierre mensual: cero inconsistencias antes de resultados.",
    validations: [
      "Puede incluir falsos positivos si hay guías en borrador."
    ],
    expectedResult: [
      "Listado accionable de descuadres."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-ajuste-create",
      "almacenes-kardex-recalcular"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-report-entries-exits",
    name: "Entradas y salidas",
    type: "report",
    route: "/reports/entries-exits",
    locationPath: "Almacenes > Reportes > Entradas y salidas",
    generalDescription: "Resumen de movimientos de inventario por tipo y período, útil para conciliar con compras y consumos.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Almacén y fechas",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Parámetros principales.",
        "behavior": ""
      },
      {
        "name": "Totales por tipo",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Entradas vs salidas.",
        "behavior": ""
      }
    ],
    procedure: [
      "Abra Entradas y salidas.",
      "Filtre.",
      "Exporte si está disponible."
    ],
    practicalExample: "Totales de salida por consumo en obra.",
    validations: [
      "Incluye movimientos confirmados en el rango."
    ],
    expectedResult: [
      "Totales coherentes con kardex agregado."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-report-entries-exits-matrix"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-report-entries-exits-matrix",
    name: "Matriz entradas y salidas",
    type: "report",
    route: "/reports/entries-exits-matrix",
    locationPath: "Almacenes > Reportes > Matriz entradas y salidas",
    generalDescription: "Cruce producto × tipo de movimiento para análisis detallado de flujos.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Filtros almacén / fechas",
        "controlType": "Filtros",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Acota la matriz.",
        "behavior": ""
      },
      {
        "name": "Matriz",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Filas producto, columnas tipo movimiento.",
        "behavior": ""
      }
    ],
    procedure: [
      "Abra Matriz.",
      "Configure filtros.",
      "Identifique productos con un solo tipo de salida."
    ],
    practicalExample: "Ver qué insumos solo tienen salida por producción.",
    validations: [
      "Puede ser pesado con muchos productos; acote fechas."
    ],
    expectedResult: [
      "Matriz exportable o visible en pantalla."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-report-entries-exits"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-report-exits-by-supplier",
    name: "Salidas por proveedor",
    type: "report",
    route: "/reports/exits-by-supplier",
    locationPath: "Almacenes > Reportes > Salidas por proveedor",
    generalDescription: "Relaciona salidas de inventario con proveedor de origen del producto o del documento, según reglas del reporte, para análisis de devoluciones o consumo por abastecedor.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Proveedor",
        "controlType": "Select",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Filtro opcional.",
        "behavior": ""
      },
      {
        "name": "Período y almacén",
        "controlType": "Filtros",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Obligatorios o recomendados.",
        "behavior": ""
      },
      {
        "name": "Detalle",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Líneas de salida agrupadas.",
        "behavior": ""
      }
    ],
    procedure: [
      "Abra Salidas por proveedor.",
      "Filtre proveedor y fechas.",
      "Analice devoluciones o consumos."
    ],
    practicalExample: "Devolución a proveedor: cuadrar cantidades salidas.",
    validations: [
      "Depende de trazabilidad proveedor en ingresos."
    ],
    expectedResult: [
      "Reporte agrupado por proveedor."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-salida-list"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado],
    roles: ROLES.almacenes,
  }),
  functionDoc({
    id: "almacenes-report-stock",
    name: "Stock actual",
    type: "report",
    route: "/reports/stock",
    locationPath: "Almacenes > Reportes > Stock",
    generalDescription: "Consulta saldo actual por producto y almacén sin detalle cronológico del kardex.",
    prerequisites: [
      "Módulo de almacenes / kardex habilitado.",
      "Productos y unidades configurados en Gestión.",
      "Almacén seleccionado en /warehouses (usuarios limitados: solo asignados)."
    ],
    interfaceElements: [
      {
        "name": "Almacén",
        "controlType": "Campo",
        "required": true,
        "editable": true,
        "automatic": false,
        "description": "Filtro principal.",
        "behavior": ""
      },
      {
        "name": "Búsqueda producto",
        "controlType": "Filtro",
        "required": false,
        "editable": true,
        "automatic": false,
        "description": "Texto o categoría.",
        "behavior": ""
      },
      {
        "name": "Tabla stock",
        "controlType": "Tabla",
        "required": false,
        "editable": true,
        "automatic": true,
        "description": "Cantidad y valor si aplica.",
        "behavior": ""
      }
    ],
    procedure: [
      "Reportes → Stock.",
      "Seleccione almacén.",
      "Busque producto."
    ],
    practicalExample: "Verificar disponibilidad antes de despacho.",
    validations: [
      "Stock es instantánea del kardex."
    ],
    expectedResult: [
      "Saldos actuales listados."
    ],
    commonErrors: [
      {
        "error": "No tiene permiso para guardar",
        "cause": "Usuario limitado en solo lectura o sin almacén asignado.",
        "solution": "Solicite permisos al administrador o seleccione un almacén autorizado."
      },
      {
        "error": "Stock insuficiente",
        "cause": "Salida o traslado supera el saldo en kardex.",
        "solution": "Revise kardex, ingrese mercadería o ajuste según política."
      }
    ],
    relatedFunctions: [
      "almacenes-kardex"
    ],
    observations: [ROLES.limitado, "El almacén activo se elige en Almacenes antes de operar guías."],
    screenshots: [SHOTS.listado],
    roles: ROLES.almacenes,
  }),

  functionDoc({
    id: 'almacenes-grilla-productos-items',
    name: 'Grilla de productos e ítems — Almacenes',
    type: 'screen',
    route: '/warehouses/1/entry-guides/create',
    locationPath:
      'Almacenes > Documentos > Guía (ingreso, salida, traslado, recepción, ajuste, inicial) → líneas de producto',
    generalDescription:
      'En EntryGuideForm cada guía de movimiento incluye una grilla de productos con el mismo patrón visual que Compras (captura rápida, filas con ProductSelector, UnitSelect, cantidad y a menudo precio unitario para valorizar kardex). La referencia completa de controles está en Inicio y acceso → «Grilla de productos e ítems». Particularidades de almacén: puede jalar líneas desde orden de compra, documento de compra o requerimiento; toggle «Precios incluyen IGV» en guías valorizadas; plantilla Excel propia de guías; salidas y traslados validan stock; el evento de kardex depende del tipo de guía (ingreso suma, salida resta, etc.).',
    prerequisites: [
      'Almacén de trabajo seleccionado en Almacenes → Almacenes.',
      'Productos y unidades en Gestión.',
      'Permiso de escritura en guías (no solo lectura en almacén asignado).',
    ],
    interfaceElements: [
      {
        name: 'Sección de ítems de la guía',
        controlType: 'Grilla',
        required: true,
        editable: true,
        automatic: false,
        description: 'Producto, unidad, proyecto, cantidad, precio (si valoriza), IGV, subtotal.',
        behavior: 'Layout GUIDE_ITEM_ROW; captura rápida si está habilitada globalmente.',
      },
      {
        name: 'Documentos relacionados',
        controlType: 'Panel',
        required: false,
        editable: true,
        automatic: false,
        description: 'OC, documento de compra o requerimiento que originó las líneas.',
        behavior: 'Quitar referencia puede vaciar productos jalados; lea el aviso antes de confirmar.',
      },
      {
        name: 'Importar Excel de guía',
        controlType: 'Archivo',
        required: false,
        editable: true,
        automatic: false,
        description: 'Plantilla específica de ítems de guía (no la misma nombre que OC en todos los casos).',
        behavior: 'Previsualización GuideItemsImportPreviewModal antes de aplicar.',
      },
      {
        name: 'Stock en salidas',
        controlType: 'Validación',
        required: false,
        editable: false,
        automatic: true,
        description: 'En guías de salida/traslado puede mostrarse saldo disponible.',
        behavior: 'Error al guardar si cantidad supera stock (según reglas del tipo).',
      },
    ],
    procedure: [
      'Lea Inicio y acceso → Grilla de productos e ítems si es la primera vez.',
      'Abra el tipo de guía desde Documentos del almacén.',
      'Opcional: asocie OC o documento y deje que el sistema cargue líneas pendientes.',
      'Complete o ajuste cantidades, unidades y precios de valorización.',
      'Confirme la guía y verifique el movimiento en kardex.',
    ],
    practicalExample: {
      context: 'Ingreso por OC parcial.',
      data: { Guía: 'Ingreso', OC: 'OC-2026-0201' },
      steps: [
        'Nueva guía de ingreso → referencie la OC.',
        'Ajuste cantidades recibidas en la grilla.',
        'Guarde y confirme; revise kardex del producto.',
      ],
    },
    validations: [
      'Al menos un ítem con producto y cantidad > 0.',
      'Fechas y serie según tipo de guía.',
      'Stock suficiente en salidas/traslados.',
      'Cantidades no mayores al pendiente de OC si hay referencia.',
    ],
    expectedResult: [
      'Guía registrada; kardex actualizado al confirmar según tipo.',
      'Avance de OC/requerimiento actualizado si había referencia.',
    ],
    commonErrors: [
      {
        error: 'Stock insuficiente',
        cause: 'Salida mayor al saldo.',
        solution: 'Reduzca cantidad o ingrese stock antes.',
      },
      {
        error: 'Se borraron líneas al quitar referencia',
        cause: 'Acción «Quitar referencia» elimina ítems jalados.',
        solution: 'Vuelva a jalar la OC o cargue líneas manualmente.',
      },
    ],
    relatedFunctions: [
      'general-grilla-productos-items',
      'general-grilla-detalle-guia-ingreso',
      'general-grilla-detalle-guia-salida-traslado',
      'general-grilla-detalle-controles-comunes',
      'almacenes-ingreso-create',
      'compras-grilla-productos-items',
      'almacenes-kardex',
    ],
    observations: [
      'Cada columna documentada en Inicio y acceso → «Grilla — fila Guía de ingreso» y «Grilla — fila Guía salida / traslado».',
    ],
    screenshots: [SHOTS.formularioCompleto],
    roles: ROLES.almacenes,
  }),
];
